import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import TierDetailsModal from "@/components/TierDetailsModal";
import {
  ArrowRight, Activity, Target, Zap, MapPin, Users, CalendarDays,
  Search, Star, Award, Sparkles, Check, Wind
} from "lucide-react";

const TIER_ORDER = ["group", "performance", "one_on_one"];
const TIER_COLOR = { group: "#007AFF", performance: "#FF4500", one_on_one: "#CCFF00" };
const ACCENT = "#FF4500"; // Pickleball orange — primary accent
const ACCENT_2 = "#CCFF00";

/**
 * Loads the chosen Google Fonts via a single <link> tag and writes a scoped
 * CSS block so headings + body inside `.landing-root` switch to the
 * super-admin's picks. The rest of the app keeps its build-time defaults.
 *
 * Re-runs are no-ops past the first call (font choices are constant per
 * page load — change requires a refresh).
 */
const LANDING_FONT_FLAG = "__rallylab_landing_fonts_applied";
function applyLandingFonts(cfg) {
  if (!cfg || window[LANDING_FONT_FLAG]) return;
  const heading = cfg.options?.heading?.[cfg.heading_font];
  const body = cfg.options?.body?.[cfg.body_font];
  if (!heading || !body) return;  // unknown font key (out-of-sync client) — skip
  // Both defaults? Skip — already in index.css
  if (heading.default && body.default) {
    window[LANDING_FONT_FLAG] = true;
    return;
  }
  // <link> for Google Fonts (only when at least one is non-default)
  const families = [];
  if (!heading.default) families.push(`family=${encodeURIComponent(heading.family)}:wght@${heading.weights}`);
  if (!body.default && body.family !== heading.family) families.push(`family=${encodeURIComponent(body.family)}:wght@${body.weights}`);
  if (families.length) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?${families.join("&")}&display=swap`;
    link.setAttribute("data-rallylab-landing-fonts", "1");
    document.head.appendChild(link);
  }
  // Scoped CSS — only affects elements inside .landing-root
  const style = document.createElement("style");
  style.setAttribute("data-rallylab-landing-fonts-css", "1");
  style.textContent = `
    .landing-root, .landing-root p, .landing-root span, .landing-root a, .landing-root div, .landing-root li, .landing-root button { font-family: '${body.family}', sans-serif; }
    .landing-root h1, .landing-root h2, .landing-root h3, .landing-root h4, .landing-root h5, .landing-root .h-display, .landing-root .label-mono { font-family: '${heading.family}', sans-serif; }
  `;
  document.head.appendChild(style);
  window[LANDING_FONT_FLAG] = true;
}

// Client-side fallbacks — used only when /api/site-settings/landing is
// unreachable (network glitch) so the hero never renders blank.
const FALLBACK_SLOGANS = [
  ["MASTER THE", "{KITCHEN}.", "WIN THE", "[THIRD SHOT]."],
];
const FALLBACK_IMAGES = [
  { src: "https://images.unsplash.com/photo-1693142518820-78d7a05f1546?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000", alt: "Pickleball paddles and balls on the court" },
];

function bucketIndex(mode, listLen) {
  // Deterministic-per-visitor rotation logic. Falls back to weekly on any
  // unknown mode so a corrupted setting never crashes the hero.
  if (!listLen) return 0;
  const now = Date.now();
  const MS = 60 * 60 * 1000;
  if (mode === "48h") return Math.floor(now / (48 * MS)) % listLen;
  if (mode === "random") return Math.floor(Math.random() * listLen);
  // default: weekly
  return Math.floor(now / (7 * 24 * MS)) % listLen;
}

function renderSlogan(lines) {
  // Tokens wrapped in {…} get ACCENT, [...] get ACCENT_2. Punctuation after the bracket stays plain.
  return lines.map((line, i) => {
    const m1 = line.match(/^\{(.+?)\}(.*)$/);
    if (m1) return <span key={i}><span style={{ color: ACCENT }}>{m1[1]}</span>{m1[2]}</span>;
    const m2 = line.match(/^\[(.+?)\](.*)$/);
    if (m2) return <span key={i}><span style={{ color: ACCENT_2 }}>{m2[1]}</span>{m2[2]}</span>;
    return <span key={i}>{line}</span>;
  });
}

export default function Landing() {
  const [offerings, setOfferings] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [activeTier, setActiveTier] = useState("one_on_one");
  const [openTier, setOpenTier] = useState(null);  // tier id when modal open
  const [landingCfg, setLandingCfg] = useState(null);  // full /site-settings/landing response
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";

  // Merge built-in + custom lists; skip any built-ins the admin has
  // disabled via /admin/landing → "Include in rotation" checkboxes.
  const { sloganLines, heroImage } = useMemo(() => {
    const disabledS = new Set(landingCfg?.disabled_slogan_indexes || []);
    const disabledI = new Set(landingCfg?.disabled_image_indexes || []);
    const slogans = landingCfg
      ? [
          ...(landingCfg.builtin_slogans || [])
            .map((lines, i) => (disabledS.has(i) ? null : { lines }))
            .filter(Boolean),
          ...(landingCfg.custom_slogans || []),
        ]
      : FALLBACK_SLOGANS.map((lines) => ({ lines }));
    const images = landingCfg
      ? [
          ...(landingCfg.builtin_images || [])
            .map((img, i) => (disabledI.has(i) ? null : img))
            .filter(Boolean),
          ...(landingCfg.custom_images || []),
        ]
      : FALLBACK_IMAGES;
    const sMode = landingCfg?.slogan_mode || "weekly";
    const iMode = landingCfg?.image_mode || "weekly";
    const sIdx = sMode === "fixed" && Number.isInteger(landingCfg?.slogan_index)
      ? Math.max(0, Math.min(landingCfg.slogan_index, slogans.length - 1))
      : bucketIndex(sMode, slogans.length);
    const iIdx = iMode === "fixed" && Number.isInteger(landingCfg?.image_index)
      ? Math.max(0, Math.min(landingCfg.image_index, images.length - 1))
      : bucketIndex(iMode, images.length);
    const chosenImg = images[iIdx] || FALLBACK_IMAGES[0];
    const src = chosenImg.src?.startsWith("/uploads/") ? `${backendUrl}${chosenImg.src}` : chosenImg.src;
    return {
      sloganLines: slogans[sIdx]?.lines || FALLBACK_SLOGANS[0],
      heroImage: { ...chosenImg, src },
    };
  }, [landingCfg, backendUrl]);

  useEffect(() => {
    api.get(`/public/offerings?sport=pickleball&limit=6`).then((r) => setOfferings(r.data)).catch(() => setOfferings([]));
    // /api/pricing returns sport-segmented data ({pickleball:{...}, squash:{...}}). Unwrap to the
    // pickleball tier dict so the rest of this page can read pricing[activeTier] directly.
    api.get("/pricing").then((r) => setPricing(r.data?.pickleball || r.data)).catch(() => setPricing(null));
    // Super-admin-configurable typography: read once, inject Google Fonts link
    // + scoped override CSS. Falls back silently to the build defaults if the
    // endpoint is unreachable.
    api.get("/site-settings/landing").then((r) => {
      setLandingCfg(r.data);
      applyLandingFonts(r.data);
    }).catch(() => {});
  }, []);

  const tier = pricing?.[activeTier];

  return (
    <div className="landing-root min-h-screen bg-[#0A0A0A] text-white noise-bg overflow-hidden" data-testid="landing-root" data-heading-font={landingCfg?.heading_font || ""} data-body-font={landingCfg?.body_font || ""}>
      {/* Nav */}
      <header className="relative z-10 px-5 sm:px-8 py-5 sm:py-6 flex items-center justify-between max-w-7xl mx-auto gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 flex items-center justify-center shrink-0" style={{ background: ACCENT }}>
            <Zap strokeWidth={2.5} className="w-5 h-5 text-black" />
          </div>
          <span className="h-display text-xl sm:text-2xl tracking-tighter">RALLY LAB</span>
          <span className="label-mono ml-2 text-[#A3A3A3] hidden md:inline">PICKLEBALL</span>
        </div>
        <nav className="flex items-center gap-3 sm:gap-6 shrink-0">
          <a href="#sessions" className="hidden md:inline text-sm text-[#A3A3A3] hover:text-white uppercase tracking-wider">Sessions</a>
          <Link to="/festival" data-testid="nav-festival-events" className="hidden md:inline text-sm text-[#A3A3A3] hover:text-white uppercase tracking-wider">Festival Events</Link>
          <a href="#how" className="hidden md:inline text-sm text-[#A3A3A3] hover:text-white uppercase tracking-wider">How it works</a>
          <a href="#coach" className="hidden md:inline text-sm text-[#A3A3A3] hover:text-white uppercase tracking-wider">For coaches</a>
          <Link to="/login" data-testid="nav-login" className="text-xs sm:text-sm text-[#A3A3A3] hover:text-white uppercase tracking-wider">Sign in</Link>
          <Link to="/register" data-testid="nav-register" className="px-3 py-2 sm:px-5 sm:py-2.5 text-[10px] sm:text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ background: ACCENT, color: "#0A0A0A" }}>
            Join<span className="hidden sm:inline"> as a player</span>
          </Link>
        </nav>
      </header>

      {/* Hero — Pickleball-focused */}
      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-10 sm:pt-16 pb-16 sm:pb-24 grid-lines">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="label-mono mb-6 flex items-center gap-2" style={{ color: ACCENT }}>
              <span className="w-2 h-2" style={{ background: ACCENT }} />
              PICKLEBALL · PERFORMANCE COACHING
            </div>
            <h1 className="h-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tighter mb-6 sm:mb-8 break-words" data-testid="hero-slogan">
              {sloganLines.map((line, i) => (
                <span key={i}>
                  {renderSlogan([line])}
                  {i < sloganLines.length - 1 && <br/>}
                </span>
              ))}
            </h1>
            <p className="text-[#A3A3A3] text-lg max-w-xl mb-10 leading-relaxed">
              The performance OS built for pickleball players who want to improve faster than the rec-league crowd. Verified coaches, structured skill tracking on 10 pickleball-specific fundamentals, and a personalised AI report every 4 weeks. <span className="text-white font-semibold">From £8 a session.</span>
            </p>
            <div className="flex gap-4 items-center flex-wrap">
              <Link to="/register" data-testid="hero-cta" className="px-7 py-4 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider" style={{ background: ACCENT, color: "#0A0A0A" }}>
                Find a session <ArrowRight strokeWidth={2} className="w-4 h-4" />
              </Link>
              <a href="#sessions" className="px-7 py-4 border border-[#262626] hover:border-white text-sm uppercase tracking-wider transition-colors">
                Browse open sessions
              </a>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              <div><div className="h-display text-4xl" style={{ color: ACCENT }}>10</div><div className="label-mono mt-1">skills tracked</div></div>
              <div><div className="h-display text-4xl">4-WK</div><div className="label-mono mt-1">AI reports</div></div>
              <div><div className="h-display text-4xl" style={{ color: ACCENT_2 }}>£8</div><div className="label-mono mt-1">from / session</div></div>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up d2">
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden border border-[#262626]">
                <img
                  src={heroImage.src}
                  alt={heroImage.alt}
                  data-testid="hero-image"
                  className="w-full h-full object-cover grayscale contrast-125"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 card-surface p-5 max-w-[260px]">
                <div className="label-mono mb-2" style={{ color: ACCENT }}>YOUR NEXT SESSION</div>
                <div className="font-semibold text-base leading-tight">Third-shot drops · kitchen resets</div>
                <div className="mt-3 flex items-center gap-2 text-xs" style={{ color: ACCENT }}>
                  <Activity className="w-3.5 h-3.5" strokeWidth={2} /> SKILL TRACKING ON
                </div>
              </div>
              <div className="absolute -top-4 -right-4 p-4 w-32" style={{ background: ACCENT, color: "#0A0A0A" }}>
                <div className="label-mono text-black/70">BOOKED</div>
                <div className="h-display text-3xl leading-none">06</div>
                <div className="text-xs font-semibold">this week</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The 10 fundamentals */}
      <section className="relative z-10 max-w-7xl mx-auto px-8 py-20 border-t border-[#262626]">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="label-mono mb-4">01 / WHAT WE TRACK</div>
            <h2 className="h-display text-4xl md:text-5xl tracking-tighter mb-6">
              Ten pickleball<br/>fundamentals. <span style={{ color: ACCENT }}>Tracked.</span>
            </h2>
            <p className="text-[#A3A3A3] leading-relaxed mb-6 text-sm">
              Every session your coach rates you on the ten skills that decide pickleball matches. You get the dashboard, your coach gets the data, the AI writes the report.
            </p>
            <div className="border border-[#262626] p-5">
              <div className="label-mono mb-2" style={{ color: ACCENT }}>WHY IT WORKS</div>
              <div className="text-sm text-[#E5E5E5]">Most players plateau because they practise the same shots. Rally Lab surfaces your weakest fundamental in numbers — so you and your coach attack it on purpose.</div>
            </div>
          </div>
          <div className="md:col-span-7 grid grid-cols-2 gap-px bg-[#262626]">
            {[
              { t: "Serve", d: "Depth, placement, spin" },
              { t: "Return", d: "Deep, soft, repeatable" },
              { t: "Dink", d: "Cross-court & straight" },
              { t: "Third Shot Drop", d: "The match-defining shot" },
              { t: "Volley", d: "Punch volleys & roll" },
              { t: "Reset", d: "Out of trouble, into kitchen" },
              { t: "Footwork", d: "Splits, shuffles, recovery" },
              { t: "Kitchen Play", d: "Patience at the NVZ" },
              { t: "Mental", d: "Score pressure & composure" },
              { t: "Fitness", d: "Endurance & explosiveness" },
            ].map((s, i) => (
              <div key={s.t} className="bg-[#0A0A0A] p-5 hover:bg-[#141414] transition-colors">
                <div className="flex items-center gap-2 mb-1">
                  <span className="label-mono" style={{ color: i % 2 ? ACCENT_2 : ACCENT }}>{String(i+1).padStart(2,"0")}</span>
                </div>
                <div className="text-base font-semibold mb-0.5">{s.t}</div>
                <div className="text-xs text-[#A3A3A3]">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Sessions — student-first */}
      <section id="sessions" className="relative z-10 max-w-7xl mx-auto px-8 py-20 border-t border-[#262626]">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div className="label-mono mb-4">02 / OPEN SESSIONS</div>
            <h2 className="h-display text-4xl md:text-5xl tracking-tighter">Book one starting <span style={{ color: ACCENT }}>this week</span>.</h2>
          </div>
          <div className="label-mono" style={{ color: ACCENT }}>PICKLEBALL · ALL LEVELS</div>
        </div>

        {!offerings.length ? (
          <div className="card-surface p-8 sm:p-12 text-center">
            <Search className="w-10 h-10 text-[#262626] mx-auto mb-4" strokeWidth={1.5} />
            <div className="h-display text-2xl mb-1">No open pickleball sessions yet</div>
            <div className="text-sm text-[#A3A3A3] mb-6">Sign up and post a tailored request — when 3+ players share your level &amp; dates, we&apos;ll build a session for you.</div>
            <Link to="/register" className="px-5 py-3 text-xs inline-flex items-center gap-2 font-bold uppercase tracking-wider" style={{ background: ACCENT, color: "#0A0A0A" }}>
              Request a tailored session <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#262626]">
            {offerings.map((o) => {
              const d = new Date(o.start);
              return (
                <div key={o.id} className="bg-[#0A0A0A] p-6 hover:bg-[#141414] transition-colors group">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5" style={{ background: ACCENT }} />
                      <span className="label-mono" style={{ color: ACCENT }}>PICKLEBALL</span>
                      <span className="label-mono">{o.skill_level}</span>
                    </div>
                    <div className="text-right">
                      <div className="label-mono text-[0.55rem]">FROM</div>
                      <div className="h-display text-2xl" style={{ color: ACCENT }}>£{o.price_per_player}</div>
                    </div>
                  </div>
                  <h3 className="h-display text-xl tracking-tight mb-3 leading-tight">{o.title}</h3>
                  <div className="space-y-1.5 text-xs text-[#A3A3A3] mb-4">
                    <div className="flex items-center gap-1.5"><CalendarDays className="w-3 h-3" />{d.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                    <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" />{o.venue}</div>
                    <div className="flex items-center gap-1.5"><Users className="w-3 h-3" />{o.spaces_left} of {o.max_spaces} spaces left</div>
                  </div>
                  <Link to="/register" className="text-xs uppercase tracking-wider inline-flex items-center gap-1 group-hover:gap-2 transition-all" style={{ color: ACCENT }}>
                    Sign in to enrol <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* How it works */}
      <section id="how" className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20 border-t border-[#262626]">
        <div className="label-mono mb-4">03 / HOW IT WORKS</div>
        <h2 className="h-display text-3xl sm:text-4xl md:text-5xl mb-10 sm:mb-12 max-w-2xl tracking-tighter">From signup to a personalised report — in three steps.</h2>
        <div className="grid md:grid-cols-3 gap-px bg-[#262626]">
          {[
            { n: "01", t: "Sign up free", d: "Tell us your level (beginner, intermediate, advanced). No credit card needed to start.", icon: Users },
            { n: "02", t: "Book or request a session", d: "Choose an open session that fits, or post a tailored request — we group you with other players at your level.", icon: CalendarDays },
            { n: "03", t: "Track your game", d: "After each session your coach rates 10 skills. After 4 weeks you get an AI report with strengths, weaknesses and next focus.", icon: Target },
          ].map(({ n, t, d, icon: I }, i) => (
            <div key={n} className={`bg-[#0A0A0A] p-6 sm:p-8 fade-up d${i+1}`}>
              <div className="flex items-start justify-between mb-6">
                <span className="label-mono" style={{ color: ACCENT }}>{n}</span>
                <I className="w-5 h-5 text-[#A3A3A3]" strokeWidth={1.5} />
              </div>
              <h3 className="h-display text-2xl mb-2 tracking-tight">{t}</h3>
              <p className="text-sm text-[#A3A3A3] leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-20 border-t border-[#262626]" id="pricing">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="label-mono mb-4">04 / WHAT IT COSTS</div>
            <h2 className="h-display text-4xl md:text-5xl tracking-tighter max-w-2xl">Three ways to coach with us.</h2>
          </div>
          <div className="inline-flex border border-[#262626]" data-testid="pricing-toggle">
            {TIER_ORDER.map((t) => {
              const active = activeTier === t;
              return (
                <button key={t} data-testid={`tier-${t}`}
                  onClick={() => { setActiveTier(t); setOpenTier(t); }}
                  className="px-5 py-3 text-xs uppercase tracking-wider transition-colors"
                  style={active ? { background: TIER_COLOR[t], color: "#0A0A0A", fontWeight: 700 } : { color: "#A3A3A3" }}>
                  {pricing?.[t]?.label?.split(" ")[0] || t}
                </button>
              );
            })}
          </div>
        </div>

        {tier && (
          <div className="grid md:grid-cols-12 gap-px bg-[#262626] fade-up">
            <div className="md:col-span-7 bg-[#0A0A0A] p-6 sm:p-8 md:p-10">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div className="min-w-0">
                  <div className="label-mono mb-2" style={{ color: TIER_COLOR[activeTier] }}>{activeTier.replace("_", " ").toUpperCase()}</div>
                  <h3 className="h-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tighter">{tier.label}</h3>
                </div>
                <div className="text-right shrink-0">
                  <div className="label-mono">FROM</div>
                  <div className="h-display text-4xl sm:text-5xl md:text-6xl tracking-tighter" style={{ color: TIER_COLOR[activeTier] }}>£{tier.from_price}</div>
                  <div className="text-xs text-[#A3A3A3] uppercase tracking-wider">{tier.unit}</div>
                </div>
              </div>
              <p className="text-[#A3A3A3] leading-relaxed mb-8 max-w-xl">{tier.tagline}</p>
              <ul className="space-y-3 mb-8">
                {(tier.bullets || []).map((b) => (
                  <li key={b} className="flex items-center gap-3 text-sm">
                    <Check className="w-4 h-4" style={{ color: TIER_COLOR[activeTier] }} strokeWidth={2.5} />
                    {b}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-3 items-center">
                <Link to="/register" data-testid={`tier-cta-${activeTier}`} className="px-7 py-4 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider" style={{ background: TIER_COLOR[activeTier], color: "#0A0A0A" }}>
                  Start with {tier.label.split(" ")[0]} <ArrowRight className="w-4 h-4" />
                </Link>
                <button
                  type="button"
                  onClick={() => setOpenTier(activeTier)}
                  data-testid={`tier-learn-${activeTier}`}
                  className="px-5 py-4 text-xs uppercase tracking-wider border border-[#262626] hover:border-white transition-colors inline-flex items-center gap-2"
                >
                  Full tier breakdown <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="mt-4 text-[10px] text-[#525252] uppercase tracking-wider max-w-md">
                &quot;From&quot; prices set by Rally Lab. Individual coaches set their own rates — see them on each <Link to="/find-a-coach" className="text-[#CCFF00] hover:underline">coach profile</Link>.
              </div>
            </div>
            <div className="md:col-span-5 grid grid-rows-2 gap-px bg-[#262626]">
              {TIER_ORDER.filter((t) => t !== activeTier).map((t) => {
                const x = pricing[t];
                return (
                  <button key={t} onClick={() => { setActiveTier(t); setOpenTier(t); }}
                    className="bg-[#0A0A0A] p-6 text-left hover:bg-[#141414] transition-colors group" data-testid={`tier-card-${t}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="label-mono mb-2" style={{ color: TIER_COLOR[t] }}>{t.replace("_", " ")}</div>
                        <div className="h-display text-2xl tracking-tight mb-1">{x.label}</div>
                        <div className="text-xs text-[#A3A3A3] line-clamp-2">{x.tagline}</div>
                      </div>
                      <div className="text-right shrink-0 ml-4">
                        <div className="label-mono text-[0.6rem]">FROM</div>
                        <div className="h-display text-3xl" style={{ color: TIER_COLOR[t] }}>£{x.from_price}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* Trust strip */}
      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-16 border-t border-[#262626]">
        <div className="grid md:grid-cols-3 gap-px bg-[#262626]">
          {[
            { i: Award, t: "Verified coaches", d: "Every coach is admin-reviewed for certification and insurance before they can offer sessions." },
            { i: Star, t: "Real progress data", d: "10 pickleball-specific skills rated after every session. No vibes, just numbers." },
            { i: Sparkles, t: "AI 4-week reports", d: "Powered by Claude Sonnet 4.5. Strengths, weaknesses, what to focus on next." },
          ].map(({ i: I, t, d }) => (
            <div key={t} className="bg-[#0A0A0A] p-6 flex gap-4 items-start">
              <I className="w-5 h-5 mt-1 shrink-0" style={{ color: ACCENT }} strokeWidth={1.5} />
              <div>
                <div className="font-semibold mb-1">{t}</div>
                <div className="text-sm text-[#A3A3A3]">{d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24 border-t border-[#262626] text-center">
        <h2 className="h-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-6 tracking-tighter">READY TO WIN MORE KITCHEN BATTLES?</h2>
        <p className="text-[#A3A3A3] mb-8 max-w-xl mx-auto">Free to join. Find a session, post a request, or just start tracking. Cancel anytime.</p>
        <Link to="/register" data-testid="footer-cta" className="px-8 py-4 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider" style={{ background: ACCENT, color: "#0A0A0A" }}>
          Create my player account <ArrowRight className="w-4 h-4" />
        </Link>
      </section>

      {/* For coaches strip — separated, lower-key */}
      <section id="coach" className="relative z-10 border-t border-[#262626] bg-[#141414]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="label-mono mb-4 flex items-center gap-2"><Award className="w-3.5 h-3.5" style={{ color: ACCENT }} /> FOR COACHES</div>
            <h2 className="h-display text-2xl sm:text-3xl md:text-4xl tracking-tighter mb-4">
              Pickleball coach? Apply to join the network.
            </h2>
            <p className="text-[#A3A3A3] mb-6 max-w-2xl text-sm leading-relaxed">
              Rally Lab gives serious pickleball coaches an end-to-end OS — calendar bookings, skill tracking, AI player reports, tailored-session matching and revenue analytics. Every coach is verified: valid certification &amp; active insurance, reviewed by our admin team within 48h.
            </p>
            <div className="flex items-center gap-4 flex-wrap">
              <Link to="/register?role=coach" data-testid="coach-cta" className="px-6 py-3 border hover:bg-[#FF4500] hover:text-black uppercase tracking-wider text-xs transition-colors inline-flex items-center gap-2" style={{ borderColor: ACCENT, color: ACCENT }}>
                Apply to coach <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <Link to="/login" className="text-xs text-[#A3A3A3] hover:text-white uppercase tracking-wider">Existing coach? Sign in</Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-3">
              {[
                { k: "OS", v: "Bookings, calendar, players" },
                { k: "AI", v: "4-week reports per player" },
                { k: "MATCH", v: "Auto-cluster requests" },
                { k: "PAID", v: "Pricing per tier" },
              ].map(({ k, v }) => (
                <div key={k} className="border border-[#262626] p-4">
                  <div className="label-mono" style={{ color: ACCENT }}>{k}</div>
                  <div className="text-sm mt-1">{v}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 text-[0.65rem] text-[#A3A3A3] flex items-center gap-2">
              <Wind className="w-3 h-3" /> Multi-sport coaches (e.g. squash) — additional sports can be enabled by an admin from your settings after approval.
            </div>
          </div>
        </div>
      </section>

      {/* Iter310 — Festival Organiser section. Lives BELOW the "For coaches"
          block (which is the last content section) and ABOVE the footer.
          Keeps the coaching + player content on the page 100% unchanged. */}
      <section id="festival" className="relative z-10 border-t border-[#262626] bg-[#0A0A0A]" data-testid="landing-festival-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="label-mono mb-4" style={{ color: ACCENT }}>
                <span className="inline-block w-2 h-2 mr-2" style={{ background: ACCENT }} /> FESTIVAL LIVE OPS
              </div>
              <h2 className="h-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tighter mb-5">
                Running a festival? <span style={{ color: ACCENT }}>Do it on one screen.</span>
              </h2>
              <p className="text-[#A3A3A3] text-base leading-relaxed mb-6">
                Live court engine, big-screen public wall viewer, player self-service and helper seats — everything you need to run a smooth pickleball or squash festival without spreadsheets or shouting across the venue.
              </p>
              <ul className="text-sm text-[#E5E5E5] space-y-2 mb-8">
                <li>· Real-time multi-court scheduling with automatic timers</li>
                <li>· Public viewer + QR sign-in for players and spectators</li>
                <li>· Rotating sponsor ribbon for on-court commercial activation</li>
                <li>· Basket checkout with vouchers · POA quotes for bespoke events</li>
              </ul>
              <div className="flex gap-3 items-center flex-wrap">
                <Link to="/register?as=organiser" data-testid="landing-festival-cta-signup" className="px-6 py-3 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider" style={{ background: ACCENT, color: "#0A0A0A" }}>
                  Create organiser account <ArrowRight strokeWidth={2} className="w-4 h-4" />
                </Link>
                <Link to="/login?as=organiser" data-testid="landing-festival-cta-login" className="px-6 py-3 border border-[#525252] hover:border-white text-sm uppercase tracking-wider transition-colors">
                  Log in
                </Link>
                <Link to="/festival" data-testid="landing-festival-cta-pricing" className="text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white">
                  See all tiers →
                </Link>
              </div>
            </div>
            <div className="lg:col-span-7 grid gap-3 sm:grid-cols-2" data-testid="landing-festival-tiers">
              {[
                { name: "Starter", cap: "Up to 50 players",  price: "£75",  duration: "1-day", accent: ACCENT,  desc: "1 helper · self-service bolt-on" },
                { name: "Standard", cap: "Up to 180 players", price: "£120", duration: "1-day", accent: "#66C2FF", desc: "3 helpers · 1 OPS · 1 deputy admin" },
                { name: "Pro",     cap: "Up to 500 players", price: "£200", duration: "1-day", accent: "#FFB020", desc: "5 helpers · 2 OPS · self-service included", highlight: true },
                { name: "Unlimited", cap: "500+ players",     price: "POA",  duration: "All durations", accent: "#B26BFF", desc: "Owner-configured seats · manual activation" },
              ].map((t) => (
                <div key={t.name} className="card-surface p-5 relative" style={t.highlight ? { borderColor: t.accent } : undefined} data-testid={`landing-festival-tier-${t.name.toLowerCase()}`}>
                  {t.highlight && (
                    <div className="absolute -top-3 left-4 px-2 py-0.5 text-[9px] uppercase tracking-widest font-bold" style={{ background: t.accent, color: "#0A0A0A" }}>Most popular</div>
                  )}
                  <div className="label-mono" style={{ color: t.accent }}>{t.name.toUpperCase()}</div>
                  <div className="text-xs text-[#A3A3A3] mt-1">{t.cap}</div>
                  <div className="mt-4 flex items-baseline gap-2">
                    <div className="h-display text-3xl text-white">{t.price}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#525252]">{t.duration}</div>
                  </div>
                  <div className="mt-3 text-[11px] text-[#A3A3A3]">{t.desc}</div>
                </div>
              ))}
              <div className="col-span-full text-[10px] uppercase tracking-widest text-[#525252] mt-1">
                Prices shown are 1-day starters. Standard/Pro 2-3 day and Unlimited are Price-On-Application.
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-[#262626] py-6 sm:py-8 px-5 sm:px-8 max-w-7xl mx-auto flex flex-wrap justify-between items-center gap-2 text-xs text-[#A3A3A3]">
        <div className="label-mono">© RALLY LAB · 2026</div>
        <div className="label-mono">PICKLEBALL · PERFORMANCE OS · FESTIVAL LIVE OPS</div>
      </footer>

      {/* Tier overview modal — opens when a player clicks any pricing tier card or "Full tier breakdown" */}
      <TierDetailsModal tierId={openTier} onClose={() => setOpenTier(null)} />
    </div>
  );
}
